import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Menu, X, User, Briefcase, ShieldCheck, LogIn } from "lucide-react";

const NAV = [
  { label: "Why Us", href: "#why-us" },
  { label: "Services", href: "#services" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Trust", href: "#trust" },
  { label: "Careers", href: "/careers", external: true },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-background/80 border-b border-border/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground font-serif-display text-lg">
            C
          </span>
          <span className="font-serif-display text-xl md:text-2xl tracking-tight text-foreground">
            Care Anywhere
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.href.replace(/[#\/]/g, "") || "home"}`}
              className="text-sm text-muted-foreground hover:text-primary transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-primary after:transition-all hover:after:w-full"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="header-signin-menu"
                className="rounded-full h-11 px-5 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary"
              >
                <LogIn size={14} className="mr-1.5" />
                Sign in
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-muted-foreground">Choose your portal</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="signin-parent" onSelect={() => { window.location.href = "/portal/login"; }} className="cursor-pointer">
                <User size={14} className="mr-2 text-primary" /> Parent portal
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="signin-applicant" onSelect={() => { window.location.href = "/careers/login"; }} className="cursor-pointer">
                <Briefcase size={14} className="mr-2 text-primary" /> Caregiver applicants
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="signin-admin" onSelect={() => { window.location.href = "/admin/login"; }} className="cursor-pointer">
                <ShieldCheck size={14} className="mr-2 text-primary" /> Staff / Admin
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            asChild
            data-testid="header-cta-book"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 h-11"
          >
            <a href="#inquire">Book Care Now</a>
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-border text-foreground"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-border/70 bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="py-2 text-foreground/90"
              >
                {item.label}
              </a>
            ))}
            <Button
              asChild
              data-testid="mobile-cta-book"
              className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11"
            >
              <a href="#inquire" onClick={() => setOpen(false)}>
                Book Care Now
              </a>
            </Button>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-xs uppercase tracking-widest text-muted-foreground pb-2">Portals</div>
              <a href="/portal/login" data-testid="mobile-signin-parent" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-foreground/90"><User size={14} className="text-primary" /> Parent portal</a>
              <a href="/careers/login" data-testid="mobile-signin-applicant" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-foreground/90"><Briefcase size={14} className="text-primary" /> Caregiver applicants</a>
              <a href="/admin/login" data-testid="mobile-signin-admin" onClick={() => setOpen(false)} className="flex items-center gap-2 py-2 text-foreground/90"><ShieldCheck size={14} className="text-primary" /> Staff / Admin</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
